// The properties panel's Drawing tab: the figures a drawing or modify tool takes
// before its first click (Transform, the rectangular and polar arrays).
// docs/claude/tools.md.
import { type Vec2 } from '../../shared/types';
import {
  selectedItems,
  selectedObjectsOf,
} from '../selection';
import {
  flashToolStatus,
  invalidate,
  pushHistory,
  renderProperties,
  repaint,
  state,
  vp,
} from './core';
import {
  armedIs,
  armPick,
} from './armedPick';
import { tr } from '../i18n';
import { toPlan } from './view3d';
import {
  frameMove,
  PLAN_PLANE,
  planePoint,
  sectionOf,
} from '../workPlane';
import {
  applyTilt,
  cloneTargets,
  featureSelectedIn,
  featureStuckWhy,
  moveFeaturesBy,
  moveSelectionOnce,
  planeRefusal,
  planeSnap,
  resizeLocked,
  scaleInSection,
  scaleSelection,
  sectionDisplayName,
  sectionSelectionMiddle,
  selectedFeatureMoves,
  selectionCenter,
  startTilt,
  syncDimensions,
  toolPlane,
} from '../main';
import {
  arrayPolar,
  arrayRect,
} from './arrange';
import {
  buttonRow,
  evalFormula,
  infoRow,
  numberRow,
  parseMeasure,
  sectionTitle,
} from './panelRows';

/** This module's state that main.ts reads and writes too. */
export const drawingTab = {
  // Polar-array pivot: an explicitly-picked centre (else the selection centre is
  // used), picked by an armed pick: the next canvas click.
  arrayPolarCenter: null as Vec2 | null,
  /** The polar array's centre picked in a section view: that view and its height,
   *  so the copies turn about a level line through it (they tilt). */
  arrayPolarSection: null as { id: string; z: number } | null,
  filletMode: 'fillet' as 'fillet' | 'chamfer',
  filletRadius: 100,
  arcMode: '3point' as '3point' | 'sce',
};

/** The polar array's centre, picked: the next click sets the rotation pivot, as
 *  it snaps. A centre in a section view turns the copies about a level line:
 *  they tilt. */
function armArrayCentre(): void {
  toPlan();
  armPick({
    kind: 'array-centre',
    cursor: 'crosshair',
    prompt: () => tr('Click a point to set the polar-array centre (Esc to cancel)'),
    click: (e, sp) => {
      const world = vp.screenToWorld(sp);
      const plane = toolPlane(world);
      const pt = planePoint(plane, planeSnap(world, plane, null, e.altKey, { except: null }));
      drawingTab.arrayPolarCenter = pt.at;
      drawingTab.arrayPolarSection = plane.kind === 'section' && pt.z != null ? { id: plane.sec.id, z: pt.z } : null;
    },
    // A first point snaps alike in every plane; which one is the click's to find.
    move: (e, sp) => {
      if (state.dragMode !== 'none') return false;
      state.hoverWorld = planeSnap(vp.screenToWorld(sp), PLAN_PLANE, null, e.altKey, { except: null });
      repaint();
      return true;
    },
    cancel: () => {},
  });
}

// ---- Drawing-tool pre-click inputs (shown in the temporary "Drawing" tab) ----
// Each is a tool's `tab`, in its row (main.ts TOOL_LIFE): the tab opens while
// that tool is armed.

function hasTransformTargets(): boolean {
  return selectedItems(state, state.drawing).length > 0;
}

// Persisted field values: the properties panel is rebuilt after every apply,
// which would otherwise wipe the inputs — so repeated Move/Array clicks would
// read empty fields. Keeping the text here lets each render repopulate them.
const transformDraft = { dx: '', dy: '', dz: '', along: '', up: '', angle: '', scale: '' };
const arrayRectDraft = { rows: '', cols: '', rowSp: '', colSp: '' };
const arrayPolarDraft = { count: '', sweep: '' };

function drawLabelledRow(label: string, ...els: HTMLElement[]): HTMLElement {
  const row = document.createElement('div'); row.className = 'prop-row';
  const l = document.createElement('label'); l.textContent = label;
  const wrap = document.createElement('div'); wrap.style.display = 'flex'; wrap.style.gap = '4px'; wrap.append(...els);
  row.append(l, wrap); return row;
}

function draftField(store: Record<string, string>, key: string, ph: string): HTMLInputElement {
  const i = document.createElement('input');
  i.type = 'text'; i.inputMode = 'decimal'; i.placeholder = tr(ph);
  i.style.flex = '1'; i.style.width = '0';
  i.value = store[key];
  i.addEventListener('input', () => { store[key] = i.value; });
  return i;
}

function renderTransformControls(box: HTMLElement): void {
  // Drilled into a section view, it works in that view's plane: along its cut and
  // up. On the plan, the move takes a rise (Δz) too.
  const plane = toolPlane(), sec = sectionOf(plane);
  box.appendChild(sectionTitle(sec ? `${tr('Transform')} · ${sectionDisplayName(sec)}` : 'Transform'));
  const mmVal = (s: string): number => parseMeasure(s || '0', 'mm') ?? 0;
  const alongUp = (): { dx: number; dy: number; dz: number } => frameMove(plane, { x: mmVal(transformDraft.along), y: mmVal(transformDraft.up) });
  // A selected feature only moves there: along its wall and up.
  if (featureSelectedIn(plane)) {
    box.appendChild(drawLabelledRow(tr('Move along / up (mm)'), draftField(transformDraft, 'along', '↔'), draftField(transformDraft, 'up', '↑')));
    box.appendChild(buttonRow('Move', () => {
      const feats = selectedFeatureMoves(), { dx, dy, dz } = alongUp();
      if (!feats || (!dx && !dy && !dz)) return;
      if (!feats.length) { flashToolStatus(featureStuckWhy()); return; }
      pushHistory();
      moveFeaturesBy(feats, dx, dy, dz);
      syncDimensions(); renderProperties(); invalidate();
    }));
    return;
  }
  if (sec) box.appendChild(drawLabelledRow(tr('Move along / up (mm)'), draftField(transformDraft, 'along', '↔'), draftField(transformDraft, 'up', '↑')));
  else box.appendChild(drawLabelledRow(tr('Move Δ (mm)'), draftField(transformDraft, 'dx', 'Δx'), draftField(transformDraft, 'dy', 'Δy'), draftField(transformDraft, 'dz', 'Δz')));
  const delta = (): { dx: number; dy: number; dz: number } =>
    sec ? alongUp() : { dx: mmVal(transformDraft.dx), dy: mmVal(transformDraft.dy), dz: mmVal(transformDraft.dz) };
  const mv = document.createElement('div'); mv.style.display = 'flex'; mv.style.gap = '4px'; mv.style.margin = '6px 0';
  const btnMove = document.createElement('button'); btnMove.textContent = tr('Move'); btnMove.style.flex = '1';
  const btnCopy = document.createElement('button'); btnCopy.textContent = tr('Copy'); btnCopy.style.flex = '1';
  btnMove.addEventListener('click', () => {
    const { dx, dy, dz } = delta();
    if (!dx && !dy && !dz) return;
    // What the selected rooms carry goes along, as with every move (moveSession.ts).
    moveSelectionOnce((s) => s.moveTo({ x: dx, y: dy, z: dz }), selectedItems(state, state.drawing));
  });
  // Copy leaves the original in place and drops a copy offset by the typed Δ (mm).
  btnCopy.addEventListener('click', () => {
    const { dx, dy, dz } = delta();
    if (!dx && !dy && !dz) return;
    if (!hasTransformTargets()) return;
    pushHistory();
    cloneTargets((p) => ({ x: p.x + dx, y: p.y + dy }), 0, dz);
    syncDimensions(); renderProperties(); invalidate();
  });
  mv.append(btnMove, btnCopy); box.appendChild(mv);

  const ang = draftField(transformDraft, 'angle', '°');
  const rotRow = drawLabelledRow(tr('Rotate (°)'), ang);
  const btnRot = document.createElement('button'); btnRot.textContent = tr('Go'); btnRot.style.width = '34px';
  (rotRow.lastChild as HTMLElement).append(btnRot);
  btnRot.addEventListener('click', () => {
    const deg = evalFormula(transformDraft.angle || '0') ?? 0;
    const c = selectionCenter();
    if (!deg || !c) return;
    if (sec) {
      // In a section view: a tilt about the level line through the selection's middle there.
      const why = planeRefusal(plane, true);
      const mid = sectionSelectionMiddle(sec);
      if (why) { flashToolStatus(why); return; }
      if (!mid) return;
      pushHistory();
      applyTilt(startTilt(sec, mid), deg);
      syncDimensions(); renderProperties(); invalidate();
      return;
    }
    moveSelectionOnce((s) => s.turnTo(deg, c), selectedItems(state, state.drawing));
  });
  box.appendChild(rotRow);

  const fac = draftField(transformDraft, 'scale', '×');
  const scRow = drawLabelledRow(tr('Scale (×)'), fac);
  const btnScale = document.createElement('button'); btnScale.textContent = tr('Go'); btnScale.style.width = '34px';
  (scRow.lastChild as HTMLElement).append(btnScale);
  btnScale.addEventListener('click', () => {
    const k = evalFormula(transformDraft.scale || '1') ?? 1;
    const c = selectionCenter();
    if (!c || k <= 0 || k === 1) return;
    let kept: number;
    if (sec) {
      kept = scaleInSection(sec, k);
    } else {
      kept = selectedObjectsOf(state, state.drawing, 'instance').filter((i) => resizeLocked(i)).length;
      scaleSelection(c, k);
    }
    if (kept) flashToolStatus(tr('{n} size-locked products kept their size.').replace('{n}', String(kept)));
  });
  box.appendChild(scRow);
}

function renderArrayRectControls(box: HTMLElement): void {
  box.appendChild(sectionTitle('Rectangular array'));
  const intVal = (s: string, d: number): number => { const v = evalFormula(s || ''); return v == null ? d : Math.round(v); };
  const mmVal = (s: string, d: number): number => parseMeasure(s || '', 'mm') ?? d;
  const rows = draftField(arrayRectDraft, 'rows', 'rows'), cols = draftField(arrayRectDraft, 'cols', 'cols');
  box.appendChild(drawLabelledRow(tr('Grid rows × cols'), rows, cols));
  const rsp = draftField(arrayRectDraft, 'rowSp', 'row mm'), csp = draftField(arrayRectDraft, 'colSp', 'col mm');
  // First field = spacing between rows (vertical), second = spacing between columns.
  box.appendChild(drawLabelledRow(tr('Spacing (mm)'), rsp, csp));
  box.appendChild(buttonRow('Create array', () => {
    const nr = intVal(arrayRectDraft.rows, 1), nc = intVal(arrayRectDraft.cols, 1);
    if (nr * nc > 1) arrayRect(nr, nc, mmVal(arrayRectDraft.rowSp, 500), mmVal(arrayRectDraft.colSp, 500));
  }));
}

function renderArrayPolarControls(box: HTMLElement): void {
  box.appendChild(sectionTitle('Polar array'));
  const intVal = (s: string, d: number): number => { const v = evalFormula(s || ''); return v == null ? d : Math.round(v); };
  const cnt = draftField(arrayPolarDraft, 'count', 'count'), sweep = draftField(arrayPolarDraft, 'sweep', '° total');
  box.appendChild(drawLabelledRow(tr('Count · sweep°'), cnt, sweep));
  // Centre of rotation: pick a point, else the selection centre is used.
  box.appendChild(infoRow('Centre', drawingTab.arrayPolarCenter
    ? `${Math.round(drawingTab.arrayPolarCenter.x)}, ${Math.round(drawingTab.arrayPolarCenter.y)}`
    : tr('selection centre (default)')));
  box.appendChild(buttonRow(armedIs('array-centre') ? '📍 Click a point on the drawing…' : '📍 Pick centre point…', armArrayCentre));
  if (drawingTab.arrayPolarCenter) box.appendChild(buttonRow('Clear centre (use selection centre)', () => {
    drawingTab.arrayPolarCenter = null; drawingTab.arrayPolarSection = null; renderProperties(); invalidate();
  }));
  box.appendChild(buttonRow('Create array', () => {
    const n = intVal(arrayPolarDraft.count, 1);
    if (n > 1) arrayPolar(n, evalFormula(arrayPolarDraft.sweep || '360') ?? 360);
  }));
}

/** The tab's hint under its fields. */
function tabHint(body: HTMLElement, msg: string): void {
  const d = document.createElement('div'); d.className = 'prop-empty'; d.style.marginTop = '10px'; d.textContent = msg; body.appendChild(d);
}

/** A tab's controls, in their own box. */
function controlsBox(body: HTMLElement, controls: (box: HTMLElement) => void): void {
  const box = document.createElement('div');
  controls(box);
  body.appendChild(box);
}

/** Fillet's tab: fillet or chamfer, and the radius or the distance. */
export function filletTab(body: HTMLElement): void {
  body.appendChild(sectionTitle('Fillet / Chamfer'));
  const modeRow = document.createElement('div'); modeRow.className = 'prop-row';
  const ml = document.createElement('label'); ml.textContent = tr('Mode');
  const sel = document.createElement('select');
  for (const [v, t] of [['fillet', 'Fillet (arc)'], ['chamfer', 'Chamfer (bevel)']] as const) {
    const o = document.createElement('option'); o.value = v; o.textContent = tr(t); if (drawingTab.filletMode === v) o.selected = true; sel.appendChild(o);
  }
  sel.addEventListener('change', () => { drawingTab.filletMode = sel.value as 'fillet' | 'chamfer'; renderProperties(); });
  modeRow.append(ml, sel); body.appendChild(modeRow);
  const setRadius = (v: number): void => { drawingTab.filletRadius = Math.max(0, v); };
  if (drawingTab.filletMode === 'chamfer') body.appendChild(numberRow('Chamfer distance (mm)', drawingTab.filletRadius, setRadius, { commit: true }));
  else body.appendChild(numberRow('Radius (mm)', drawingTab.filletRadius, setRadius, { commit: true }));
  tabHint(body, tr('Click two lines to join their corner. Value 0 just trims them to a sharp corner.'));
}

/** Arc's tab: by three points, or by its start, centre and end. */
export function arcTab(body: HTMLElement): void {
  body.appendChild(sectionTitle('Arc'));
  const row = document.createElement('div'); row.className = 'prop-row';
  const l = document.createElement('label'); l.textContent = tr('Mode');
  const sel = document.createElement('select');
  for (const [v, t] of [['3point', '3 points (start · through · end)'], ['sce', 'Start · centre · end']] as const) {
    const o = document.createElement('option'); o.value = v; o.textContent = tr(t); if (drawingTab.arcMode === v) o.selected = true; sel.appendChild(o);
  }
  sel.addEventListener('change', () => { drawingTab.arcMode = sel.value as '3point' | 'sce'; });
  row.append(l, sel); body.appendChild(row);
  tabHint(body, tr('Click three points on the canvas to draw the arc.'));
}

/** Transform's tab: what it does to the selection — drilled into a section
 *  view, to a room's or a drawn wall's selected feature too. */
export function transformTab(body: HTMLElement): void {
  if (hasTransformTargets() || featureSelectedIn(toolPlane())) { controlsBox(body, renderTransformControls); return; }
  body.appendChild(sectionTitle('Transform'));
  tabHint(body, tr('Select one or more objects to transform.'));
}

/** The rectangular array's tab: its rows and columns, once something is selected. */
export function arrayRectTab(body: HTMLElement): void {
  if (hasTransformTargets()) { controlsBox(body, renderArrayRectControls); return; }
  body.appendChild(sectionTitle('Rectangular array'));
  tabHint(body, tr('Select one or more objects to array.'));
}

/** The polar array's tab: its count and sweep, once something is selected. */
export function arrayPolarTab(body: HTMLElement): void {
  if (hasTransformTargets()) { controlsBox(body, renderArrayPolarControls); return; }
  body.appendChild(sectionTitle('Polar array'));
  tabHint(body, tr('Select one or more objects to array.'));
}
